import React, { PropsWithChildren } from 'react'

const uniqueIdSet = new Set<string>()

const html4Id = /^[A-Za-z]+[\w\-:.]*$/

interface WithUniqueIdOptions {
  identifier?: string
}

interface UniqueIdTools {
  last: (id?: string) => string | null
  make: (ids: string[] | Record<string, string>) => string[] | Record<string, string> | null
  next: (id?: string) => string
}

export function clearUniqueIds() {
  uniqueIdSet.clear()
}

export function registerUniqueId(identifier: string): string {
  let num = 1
  while (uniqueIdSet.has(identifier + num)) {
    num += 1
  }
  uniqueIdSet.add(identifier + num)
  return identifier + num
}

export function unregisterUniqueId(uniqueId: string) {
  if (uniqueIdSet.has(uniqueId)) {
    uniqueIdSet.delete(uniqueId)
  }
}

interface WithUniqueIdOptions {
  identifier?: string
}

interface UniqueIdTools {
  last: (id?: string) => string | null
  make: (ids: string[] | Record<string, string>) => string[] | Record<string, string> | null
  next: (id?: string) => string
}

interface WithUniqueIdProps {
  id?: string
  uniqueId: () => UniqueIdTools
}

export function withUniqueId(options?: WithUniqueIdOptions) {
  const { identifier = 'maria-uid' } = options || {}

  if (identifier == null || !html4Id.test(identifier)) {
    throw new Error(
      '[withUniqueId] identifier must be a HTML4 compatible id string starting with ASCII letter'
    )
  }

  return function withUniqueIdWrapper<P extends object>(
    Component: React.ComponentType<P & WithUniqueIdProps>
  ) {
    class UniqueIdWrapper extends React.PureComponent<
      PropsWithChildren<Omit<P, 'uniqueId' | 'id'> & { id?: string; forwardedRef?: React.Ref<any> }>
    > {
      currentId: string | undefined

      id: string

      renderIds: Record<string, string[]> = {}

      constructor(
        props: PropsWithChildren<
          Omit<P, 'uniqueId' | 'id'> & { id?: string; forwardedRef?: React.Ref<any> }
        >
      ) {
        super(props)
        this.currentId = props.id
        this.id = registerUniqueId(props.id != null ? props.id : identifier)
      }

      lastUid = (id?: string): string | null => {
        const key = typeof id === 'string' ? id : ''
        const array = this.renderIds[key]
        return (Array.isArray(array) && array[array.length - 1]) || null
      }

      makeUids = (ids: string[] | Record<string, string>) => {
        if (Array.isArray(ids)) return ids.map(this.nextUid)
        if (ids == null || typeof ids !== 'object') return null
        return Object.keys(ids).reduce((output, key) => {
          output[key] = this.nextUid(ids[key])
          return output
        }, {} as Record<string, string>)
      }

      nextUid = (id?: string): string => {
        const key = typeof id === 'string' ? id : ''
        if (!Array.isArray(this.renderIds[key])) this.renderIds[key] = []
        const uniqueId = registerUniqueId(`${this.id}.${key}`)
        this.renderIds[key].push(uniqueId)
        return uniqueId
      }

      removeRenderGeneratedIds = () => {
        const keys = Object.keys(this.renderIds)
        for (let j = 0; j < keys.length; j += 1) {
          const key = keys[j]

          // perf: only delete if not being used in two consecutive renders
          if (this.renderIds[key].length === 0) {
            delete this.renderIds[key]
          } else {
            while (this.renderIds[key].length) {
              unregisterUniqueId(this.renderIds[key].pop()!)
            }
          }
        }
      }

      uniqueId = (): UniqueIdTools => {
        this.removeRenderGeneratedIds()
        return {
          last: this.lastUid,
          make: this.makeUids,
          next: this.nextUid,
        }
      }

      componentWillUnmount() {
        this.removeRenderGeneratedIds()
        unregisterUniqueId(this.id)
      }

      render() {
        const { forwardedRef, id, ...props } = this.props

        if (id !== this.currentId) {
          unregisterUniqueId(this.id)
          this.currentId = id
          const idValue: string = (typeof id === 'string' ? id : undefined) ?? identifier
          this.id = registerUniqueId(idValue)
        }

        return React.createElement(Component, ({
          ...props,
          ref: forwardedRef,
          id: this.id,
          uniqueId: this.uniqueId,
        } as unknown) as P & WithUniqueIdProps)
      }
    }

    return React.forwardRef<any, Omit<P, 'uniqueId' | 'id'> & { id?: string }>((props, ref) =>
      React.createElement(UniqueIdWrapper, {
        ...props,
        forwardedRef: ref,
      })
    )
  }
}
